// React
import { useCallback, useState, type ReactNode } from 'react';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';

// UI primitives
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ModalLayout, ModalTitle } from '@/components/ui/modal-layout';
import { cn } from '@/lib/utils';

const FORM_DIALOG_CLASS =
  'max-w-dialog max-h-[90vh] rounded-input border-dinnerplate-white bg-body p-0 outline-none shadow-none';

export interface FormDialogRenderProps {
  /**
   * Report whether a submit is in flight. While true, every user-initiated
   * close path is blocked.
   */
  reportPending: (isPending: boolean) => void;
  /** Close the dialog, bypassing the pending guard. Use on the success path. */
  close: () => void;
}

interface FormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  className?: string;
  children: (render: FormDialogRenderProps) => ReactNode;
}

/**
 * Modal shell for a create/edit form.
 *
 * User-initiated close paths (header X, Esc, backdrop) are gated on the
 * pending flag the form reports, so a mid-submit close can't unmount the
 * dialog while the request is in flight — the record would still be written
 * server-side while the user believes they cancelled. The success path calls
 * `close` directly and is deliberately not gated.
 *
 * The form mounts only while open, so each open starts from clean state.
 */
export function FormDialog({
  open,
  onOpenChange,
  title,
  className,
  children,
}: FormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <FormDialogContent
          onOpenChange={onOpenChange}
          title={title}
          className={className}
        >
          {children}
        </FormDialogContent>
      )}
    </Dialog>
  );
}

function FormDialogContent({
  onOpenChange,
  title,
  className,
  children,
}: Omit<FormDialogProps, 'open'>) {
  const [isPending, setIsPending] = useState(false);

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const handleUserClose = useCallback(() => {
    if (isPending) return;
    close();
  }, [close, isPending]);

  return (
    <DialogContent
      aria-describedby={undefined}
      hideCloseButton
      onEscapeKeyDown={(event) => {
        if (isPending) {
          event.preventDefault();
          return;
        }
        handleUserClose();
      }}
      onInteractOutside={(event) => {
        // Clicks inside a popper (combobox / date picker / tooltip) portal to
        // the body, so Radix reads them as "outside". Never close on those.
        const target = event.target as HTMLElement;
        if (target.closest('[data-radix-popper-content-wrapper]')) {
          event.preventDefault();
          return;
        }
        if (isPending) event.preventDefault();
      }}
      className={cn(FORM_DIALOG_CLASS, className)}
    >
      <VisuallyHidden.Root>
        <DialogTitle>{title}</DialogTitle>
      </VisuallyHidden.Root>
      <ModalLayout
        as="div"
        title={<ModalTitle>{title}</ModalTitle>}
        onClose={handleUserClose}
        contentClassName="p-0"
      >
        {children({ reportPending: setIsPending, close })}
      </ModalLayout>
    </DialogContent>
  );
}
